import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Box, Grid, Typography, Button, IconButton, Icon,
    TextField, Dialog, DialogTitle, DialogContent, DialogActions,
    List, ListItem, ListItemText, Collapse, Checkbox,
    MenuItem
} from '@material-ui/core';
import { AdminModuleService } from '@sdk-point/talisis';
import { registeredPaths } from '../../routes/componentRegistry';
import Toast from '../../design-system/components/Toast/Toast';
import LoaderContent from '../../design-system/components/Loader/LoaderContent';
import TitleBanner from '../../design-system/components/Banners/TitleBanner';
import { commonModuleStyles } from '../../assets/styles/jss/pages/common/CommonModuleStyles';

const AdminModuleServiceSDK = new AdminModuleService(
    process.env.REACT_APP_ENV_V2,
    process.env.REACT_APP_X_API_KEY_V2
);

/**
 * Genera el siguiente `module_id` jerárquico para un nuevo nodo, con el mismo
 * formato que usa la BD (ej: "1", "1.1", "1.1.2"). La columna module_id en
 * adminapp.Modules es corta, por eso NO se usan ids largos/aleatorios.
 *
 * @param {string|null} parentModuleId - module_id del padre (null = raíz).
 * @param {Array} siblings - hermanos existentes (mismo nivel), incluidos inactivos.
 * @returns {string} nuevo module_id (ej: "1.1.2" o "6").
 *
 * Toma el último segmento numérico de cada hermano, calcula el máximo y suma 1.
 * Usa el máximo (no la cantidad) para no reciclar un id ya usado tras eliminar.
 */
const nextModuleId = (parentModuleId, siblings = []) => {
    let maxLast = 0;
    for (const sib of siblings) {
        const id = String(sib.module_id || '');
        const lastSegment = id.split('.').pop();
        const n = parseInt(lastSegment, 10);
        if (!Number.isNaN(n) && n > maxLast) maxLast = n;
    }
    const next = maxLast + 1;
    return parentModuleId ? `${parentModuleId}.${next}` : String(next);
};

// Indentación por nivel para el árbol (crece con la profundidad).
const INDENT_PER_LEVEL = 24;

/**
 * Pantalla de Gestión de Módulos
 * Ruta: /admin/administracion/gestion-menu
 * Acceso: solo super_user
 *
 * Muestra el árbol jerárquico de módulos del sistema (profundidad ilimitada,
 * igual que la Lambda/BD) y permite agregar, eliminar y reordenar mediante
 * drag-and-drop. El `path` de un ítem navegable (hoja) se elige de las rutas
 * registradas en componentRegistry para garantizar el match con el código.
 */
const ModuleManagement = () => {
    const classes = commonModuleStyles();

    // Menú lateral: su árbol vive en Redux (B2BPermissions). Tras persistir un
    // cambio aquí se hace una RECARGA COMPLETA de la página (ver persistTree) para
    // que el sidebar refleje los módulos nuevos sin expulsar al usuario de esta
    // pantalla; por eso ya no se usa loadPermissions en caliente.

    // Estado principal del árbol de módulos
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedItems, setSelectedItems] = useState(new Set());

    // Expand/collapse state
    const [expanded, setExpanded] = useState(new Set());

    // Dialog para agregar/editar.
    // addParent = nodo padre (null = módulo raíz). editingNode = nodo en edición (null = alta).
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [addParent, setAddParent] = useState(null);
    const [editingNode, setEditingNode] = useState(null);
    const [newItemName, setNewItemName] = useState('');
    const [newItemIcon, setNewItemIcon] = useState('');
    const [newItemPath, setNewItemPath] = useState('');

    // Dialog para eliminar
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    // Toast
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    // --- API ---
    const fetchModules = useCallback(async () => {
        setLoading(true);
        try {
            const response = await AdminModuleServiceSDK.getModules();
            // El backend responde { success, message, data: [...] }.
            const data = Array.isArray(response)
                ? response
                : (response?.data || response?.modules || []);
            setModules(data);
            // Expandir módulos de nivel 0 por defecto.
            setExpanded(new Set(data.map((m) => m.module_id)));
        } catch (error) {
            console.error('[ModuleManagement] Error fetching modules:', error?.response?.data || error.message || error);
            setToast({ open: true, message: 'Error al cargar los módulos', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Persiste el árbol completo en el backend y recarga el estado real.
     *
     * El backend (Lambda) NO expone operaciones individuales: solo recibe el
     * árbol entero vía postModules, lo valida nodo por nodo y asigna los
     * module_id reales. Por eso cada acción del modal (agregar/editar/eliminar)
     * construye el árbol resultante y lo manda completo de inmediato.
     *
     * @param {Array} nextModules - árbol ya actualizado por la acción.
     * @param {string} successMessage - mensaje a mostrar si el POST tiene éxito.
     * @returns {Promise<boolean>} true si persistió correctamente.
     */
    const persistTree = async (nextModules, successMessage) => {
        setLoading(true);
        try {
            // La Lambda espera el árbol como ARRAY PELADO en el body (no envuelto).
            // validateTree valida por nodo: module_id, name, order (number),
            // is_active (boolean), parent_id coherente. La Lambda mapea order->sort_order.
            await AdminModuleServiceSDK.postModules(nextModules);
            setToast({ open: true, message: successMessage, severity: 'success' });
            // Recargar desde el backend para reflejar el estado real:
            // ids reales asignados, ítems eliminados que ya no vuelven (GET solo trae activos), etc.
            setSelectedItems(new Set());
            await fetchModules();
            // Refrescar el menú lateral con RECARGA COMPLETA de la página, igual que
            // "Accesos del Sistema" (Roles.jsx). Antes se llamaba loadPermissions en
            // caliente, lo que reconstruía el árbol de rutas y hacía que el Sidebar
            // expulsara al usuario a otro módulo. La recarga mantiene la URL actual
            // (/admin/administracion/gestion-menu) y arranca con los módulos nuevos
            // sin el conflicto de redirección. El delay deja ver el toast primero.
            setTimeout(() => {
                window.location.reload();
            }, 800);
            return true;
        } catch (error) {
            console.error('[ModuleManagement] Error saving modules:', error?.response?.data || error.message || error);
            setToast({ open: true, message: 'Error al guardar los cambios', severity: 'error' });
            setLoading(false);
            return false;
        }
    };

    useEffect(() => {
        fetchModules();
    }, [fetchModules]);

    // --- Expand / Collapse ---
    const toggleExpand = (moduleId) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(moduleId)) next.delete(moduleId);
            else next.add(moduleId);
            return next;
        });
    };

    // --- Selection ---
    const toggleSelection = (moduleId) => {
        setSelectedItems((prev) => {
            const next = new Set(prev);
            if (next.has(moduleId)) next.delete(moduleId);
            else next.add(moduleId);
            return next;
        });
    };

    // --- Agregar (genérico, cualquier nivel) ---
    // parent = null → módulo raíz; parent = nodo → hijo de ese nodo.
    const openAddDialog = (parent) => {
        setEditingNode(null);
        setAddParent(parent);
        setNewItemName('');
        setNewItemIcon('');
        setNewItemPath('');
        setAddDialogOpen(true);
    };

    // --- Editar un ítem existente (precarga sus datos) ---
    const openEditDialog = (node) => {
        setEditingNode(node);
        setAddParent(null);
        setNewItemName(node.name || '');
        setNewItemIcon(node.icon || '');
        setNewItemPath(node.path || '');
        setAddDialogOpen(true);
    };

    const handleAddConfirm = async () => {
        if (!newItemName.trim()) {
            setToast({ open: true, message: 'El nombre es requerido', severity: 'warning' });
            return;
        }
        // La ruta es opcional al crear (un ítem puede empezar vacío y luego tener hijos).
        // Si se eligió una ruta, debe estar registrada. La regla "hoja sin ruta"
        // se valida al guardar (findLeafWithoutPath).
        if (newItemPath && !registeredPaths.includes(newItemPath)) {
            setToast({ open: true, message: 'La ruta seleccionada no está registrada en el sistema', severity: 'warning' });
            return;
        }

        // --- Modo EDICIÓN: actualiza el nodo manteniendo su module_id (la Lambda hace UPDATE) ---
        if (editingNode) {
            const targetId = editingNode.module_id;
            const copy = JSON.parse(JSON.stringify(modules));
            const update = (list) => {
                for (const node of list) {
                    if (node.module_id === targetId) {
                        node.name = newItemName.trim();
                        node.icon = newItemIcon.trim() || (newItemPath ? 'ri-file-line' : 'ri-folder-line');
                        node.path = newItemPath || '';
                        return true;
                    }
                    if (Array.isArray(node.children) && update(node.children)) return true;
                }
                return false;
            };
            update(copy);
            // Cierra el modal y persiste directo en el backend.
            setModules(copy);
            setAddDialogOpen(false);
            setEditingNode(null);
            await persistTree(copy, 'Elemento actualizado correctamente');
            return;
        }

        const parentId = addParent ? addParent.module_id : null;

        // Construye un nodo nuevo con module_id jerárquico calculado sobre `siblings`.
        const buildNode = (siblings) => ({
            id: null, // el backend asigna la PK
            module_id: nextModuleId(parentId, siblings),
            parent_id: parentId,
            name: newItemName.trim(),
            icon: newItemIcon.trim() || (newItemPath ? 'ri-file-line' : 'ri-folder-line'),
            path: newItemPath || '',
            order: siblings.length,
            is_active: true,
            children: [],
        });

        const copy = JSON.parse(JSON.stringify(modules));
        if (parentId === null) {
            // Módulo raíz: los hermanos son los nodos de nivel 0.
            copy.push(buildNode(copy));
        } else {
            // Buscar el padre recursivamente e insertar el hijo con id jerárquico.
            const insertInto = (list) => {
                for (const node of list) {
                    if (node.module_id === parentId) {
                        if (!Array.isArray(node.children)) node.children = [];
                        node.children.push(buildNode(node.children));
                        return true;
                    }
                    if (Array.isArray(node.children) && insertInto(node.children)) return true;
                }
                return false;
            };
            insertInto(copy);
        }

        // Expandir el padre para que se vea el nuevo hijo.
        if (parentId) {
            setExpanded((prev) => new Set(prev).add(parentId));
        }
        // Cierra el modal y persiste directo en el backend.
        setModules(copy);
        setAddDialogOpen(false);
        await persistTree(copy, 'Elemento agregado correctamente');
    };

    // --- Eliminar (soft-delete) ---
    const openDeleteDialog = () => {
        if (selectedItems.size === 0) {
            setToast({ open: true, message: 'Seleccione al menos un elemento para eliminar', severity: 'warning' });
            return;
        }
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        // Soft-delete: NO se quitan del árbol. Se marca is_active=false (recursivo)
        // para que el POST los envíe y la Lambda los desactive (toDeactivate = !is_active).
        const deactivate = (node) => {
            node.is_active = false;
            if (Array.isArray(node.children)) node.children.forEach(deactivate);
        };

        const copy = JSON.parse(JSON.stringify(modules));
        const walk = (list) => {
            list.forEach((node) => {
                if (selectedItems.has(node.module_id)) deactivate(node);
                else if (Array.isArray(node.children)) walk(node.children);
            });
        };
        walk(copy);

        const count = selectedItems.size;
        // Cierra el modal y persiste directo en el backend.
        setModules(copy);
        setDeleteDialogOpen(false);
        await persistTree(
            copy,
            count === 1 ? 'Elemento eliminado correctamente' : `${count} elementos eliminados correctamente`,
        );
    };

    // --- Render recursivo de un nodo (cualquier nivel) ---
    // Nota: el reordenamiento por arrastrar no está habilitado por ahora.
    const renderNode = (node, index, level) => {
        const isExpanded = expanded.has(node.module_id);
        const hasChildren = Array.isArray(node.children) && node.children.length > 0;
        const isInactive = node.is_active === false;
        const isRoot = level === 0;

        return (
            <div
                key={node.module_id}
                style={{ borderBottom: isRoot ? '1px solid #f0f0f0' : undefined }}
            >
                <ListItem
                    style={{
                        paddingLeft: 16 + level * INDENT_PER_LEVEL,
                        paddingTop: isRoot ? 8 : 4,
                        paddingBottom: isRoot ? 8 : 4,
                    }}
                >
                    <Checkbox
                        checked={selectedItems.has(node.module_id)}
                        onChange={() => toggleSelection(node.module_id)}
                        color="primary"
                        size="small"
                    />
                    {hasChildren && (
                        <IconButton size="small" onClick={() => toggleExpand(node.module_id)} style={{ marginRight: 4 }}>
                            <Icon className={isExpanded ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'} style={{ fontSize: isRoot ? 18 : 16 }} />
                        </IconButton>
                    )}
                    <Icon
                        className={node.icon || 'ri-folder-line'}
                        style={{ fontSize: isRoot ? 18 : 16, color: isRoot ? '#1976d2' : '#666', marginRight: 8 }}
                    />
                    <ListItemText
                        primary={node.name}
                        secondary={isInactive ? 'Marcado para eliminar' : (node.path || null)}
                        primaryTypographyProps={{
                            variant: isRoot ? 'subtitle1' : 'body2',
                            style: {
                                fontWeight: isRoot ? 600 : 400,
                                ...(isInactive ? { textDecoration: 'line-through', opacity: 0.5 } : {}),
                            },
                        }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                        style={{ flex: 'none', marginRight: 4 }}
                    />
                    {/* Botón para editar este nodo (junto al nombre) */}
                    <IconButton
                        size="small"
                        title="Editar"
                        onClick={() => openEditDialog(node)}
                        style={{ color: '#1976d2' }}
                    >
                        <Icon className="ri-pencil-fill" style={{ fontSize: 20, color: '#1976d2' }} />
                    </IconButton>
                    {/* Espaciador: empuja "Agregar hijo" al extremo derecho */}
                    <span style={{ flexGrow: 1 }} />
                    {/* Botón para agregar un hijo a este nodo */}
                    <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<Icon className="ri-add-line" />}
                        onClick={() => openAddDialog(node)}
                        style={{
                            marginLeft: 8,
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                            borderColor: '#1976d2',
                            color: '#1976d2',
                        }}
                    >
                        {isRoot ? 'Agregar submódulo' : 'Agregar hijo'}
                    </Button>
                </ListItem>

                {hasChildren && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {node.children.map((child, cIdx) =>
                                renderNode(child, cIdx, level + 1, node.module_id)
                            )}
                        </List>
                    </Collapse>
                )}
            </div>
        );
    };

    return (
        <LoaderContent loading={loading}>
            <Grid container spacing={2}>
                {/* Banner */}
                <Grid item xs={12}>
                    <TitleBanner
                        title="Gestión de Módulos"
                        desc="Administra la estructura del menú del sistema. Crea, edita y elimina módulos y submódulos en cualquier nivel."
                    />
                </Grid>

                {/* Module Tree + acciones */}
                <Grid item xs={12}>
                    <Card>
                        <Box p={2}>
                            {/* Título en su propia línea */}
                            <Typography variant="h6" className={classes.sectionTitle}>
                                Estructura del Menú
                            </Typography>

                            {/* Acciones debajo del título. Cada operación (agregar/editar/
                                eliminar) persiste directo desde su modal; no hay guardado manual. */}
                            <Box
                                display="flex"
                                alignItems="center"
                                flexWrap="wrap"
                                mb={2}
                                style={{ gap: 8 }}
                            >
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    startIcon={<Icon className="ri-add-line" />}
                                    onClick={() => openAddDialog(null)}
                                    style={{ textTransform: 'none' }}
                                >
                                    Agregar Módulo
                                </Button>
                                {selectedItems.size > 0 && (
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        size="small"
                                        startIcon={<Icon className="ri-delete-bin-line" />}
                                        onClick={openDeleteDialog}
                                        style={{ textTransform: 'none' }}
                                    >
                                        Eliminar ({selectedItems.size})
                                    </Button>
                                )}
                            </Box>

                            {modules.length === 0 && !loading && (
                                <Box py={4} textAlign="center">
                                    <Icon className="ri-folder-open-line" style={{ fontSize: 48, color: '#ccc' }} />
                                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                                        No hay módulos configurados. Haz clic en "Agregar Módulo" para comenzar.
                                    </Typography>
                                </Box>
                            )}

                            <List disablePadding>
                                {modules.map((mod, idx) => renderNode(mod, idx, 0, 'root'))}
                            </List>
                        </Box>
                    </Card>
                </Grid>
            </Grid>

            {/* Add Dialog */}
            <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingNode
                        ? `Editar "${editingNode.name}"`
                        : (addParent ? `Agregar hijo en "${addParent.name}"` : 'Agregar Módulo')}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        label="Nombre"
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        value={newItemName}
                        onChange={(e) => setNewItemName(e.target.value)}
                        autoFocus
                    />
                    <TextField
                        label="Ícono (clase remix icon, ej: ri-bar-chart-line)"
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        value={newItemIcon}
                        onChange={(e) => setNewItemIcon(e.target.value)}
                    />
                    <TextField
                        select
                        label="Ruta (opcional)"
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        value={newItemPath}
                        onChange={(e) => setNewItemPath(e.target.value)}
                        helperText="Elige la página a la que abre este ítem. Déjalo vacío si solo va a contener otros ítems."
                    >
                        <MenuItem value="">
                            <em>Sin ruta (solo contiene otros ítems)</em>
                        </MenuItem>
                        {registeredPaths.map((p) => (
                            <MenuItem key={p} value={p}>{p}</MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddDialogOpen(false)} style={{ textTransform: 'none' }}>
                        Cancelar
                    </Button>
                    <Button onClick={handleAddConfirm} color="primary" variant="contained" style={{ textTransform: 'none' }}>
                        {editingNode ? 'Guardar' : 'Agregar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Confirmar eliminación</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        ¿Está seguro de eliminar {selectedItems.size === 1 ? 'el elemento seleccionado' : `los ${selectedItems.size} elementos seleccionados`}?
                        Se marcará(n) como inactivo(s) y el cambio se aplicará de inmediato.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} style={{ textTransform: 'none' }}>
                        Cancelar
                    </Button>
                    <Button onClick={confirmDelete} color="secondary" variant="contained" style={{ textTransform: 'none' }}>
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Toast */}
            <Toast
                open={toast.open}
                severity={toast.severity}
                message={toast.message}
                onFinished={() => setToast((prev) => ({ ...prev, open: false }))}
            />
        </LoaderContent>
    );
};

export default ModuleManagement;
